/**
 * Signalements' writes, one per intent. Each is offered only with its feature
 * (the server checks again), names the `version` of the report on screen,
 * gives a reason, and carries the intent's Idempotency-Key (ADR-0016). Nothing
 * changes on screen until the server answers with the report as it now is.
 */
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import {
  POLICE_DATA_CATEGORIES,
  type PoliceDataCategory,
  type SafetyAppealDecision,
  type SafetyReportDetail,
  type SafetyReportSeverity,
  type SafetyVerdict,
} from "@wallyo/contracts/console/safety";
import type { ConsoleClient } from "../../api/client";
import {
  addReportEvidence,
  banPermanently,
  decideAppeal,
  issueVerdict,
  liftSuspension,
  placeLegalHold,
  referToPolice,
  releaseLegalHold,
  setReportSeverity,
  shareDataWithPolice,
  suspendPendingInvestigation,
} from "../../api/console-api";
import { t } from "../../i18n";
import { isOwner } from "../team/team-access";
import { activeReportHold, interimInForce } from "./report-format";

export type ReportActionKind =
  | "evidence"
  | "severity"
  | "suspend"
  | "lift"
  | "verdict"
  | "ban"
  | "appeal"
  | "police"
  | "share"
  | "hold"
  | "release";

/** The order the buttons show in. */
export const REPORT_ACTIONS: readonly ReportActionKind[] = [
  "evidence",
  "severity",
  "suspend",
  "lift",
  "verdict",
  "ban",
  "appeal",
  "police",
  "share",
  "hold",
  "release",
];

const f = (name: string) => `${name}_STAFF_FEATURE_ENABLEMENT` as StaffFeatureId;

/** The feature each action needs (shared/src/actions/definitions.ts). */
export const ACTION_FEATURE: Record<ReportActionKind, StaffFeatureId> = {
  evidence: f("HANDLE_REPORTS"),
  severity: f("SET_REPORT_SEVERITY"),
  suspend: f("SUSPEND_PENDING_INVESTIGATION"),
  lift: f("SUSPEND_PENDING_INVESTIGATION"),
  verdict: f("ISSUE_VERDICTS"),
  ban: f("BAN_PERMANENTLY"),
  appeal: f("HANDLE_APPEALS"),
  police: f("REFER_TO_POLICE"),
  share: f("SHARE_DATA_WITH_POLICE"),
  hold: f("PLACE_LEGAL_HOLDS"),
  release: f("PLACE_LEGAL_HOLDS"),
};

/** Why an action can't be offered now, or null when it can. */
export type ActionBlock = "feature" | "state" | "ownVerdict" | "ownerOnly" | "noReferral";

export type ActionOffer = { kind: ReportActionKind; block: ActionBlock | null };

/**
 * Each action, and whether it can be offered to this person on this report.
 * Only the actions whose feature they hold are listed; the report's state
 * decides the rest. The server enforces all of it again.
 */
export function reportActions(
  detail: SafetyReportDetail,
  me: { staffMemberId: string; features: readonly StaffFeatureId[] },
  now: Date,
): ActionOffer[] {
  const closed = detail.status === "closed";
  const undecided = detail.status === "open" || detail.status === "investigating";
  const state: Record<ReportActionKind, ActionBlock | null> = {
    evidence: closed ? "state" : null,
    severity: closed ? "state" : null,
    suspend: !undecided || detail.restricted ? "state" : null,
    lift: interimInForce(detail, now) ? null : "state",
    verdict: undecided ? null : "state",
    ban: detail.severity === "critical" && !closed && detail.verdict !== "permanent_ban" ? null : "state",
    appeal:
      detail.status !== "appealed" || !detail.appeal || detail.appeal.decision !== null
        ? "state"
        : detail.decidedBy?.staffMemberId === me.staffMemberId
          ? "ownVerdict"
          : null,
    police: null,
    share: !isOwner(me.features) ? "ownerOnly" : detail.policeReferrals.length === 0 ? "noReferral" : null,
    hold: activeReportHold(detail) ? "state" : null,
    release: activeReportHold(detail) ? null : "state",
  };
  return REPORT_ACTIONS.filter((kind) => me.features.includes(ACTION_FEATURE[kind])).map((kind) => ({
    kind,
    block: state[kind],
  }));
}

// ── The dialog's fields ───────────────────────────────────────────────────

export type ReportActionFields = {
  reason: string;
  /** Evidence. */
  body: string;
  evidenceUrl: string;
  severity: SafetyReportSeverity;
  verdict: Exclude<SafetyVerdict, "permanent_ban">;
  /** Told to the person: a verdict's, a ban's or an appeal decision's reason. */
  toldReason: string;
  /** `datetime-local`, the end of a temporary suspension. */
  suspendedUntil: string;
  decision: SafetyAppealDecision;
  station: string;
  officerName: string;
  officerContact: string;
  policeReference: string;
  policeReferralId: string;
  categories: PoliceDataCategory[];
  description: string;
};

export function emptyFields(detail: SafetyReportDetail): ReportActionFields {
  return {
    reason: "",
    body: "",
    evidenceUrl: "",
    severity: detail.severity,
    verdict: "warning",
    toldReason: "",
    suspendedUntil: "",
    decision: "upheld",
    station: "",
    officerName: "",
    officerContact: "",
    policeReference: "",
    policeReferralId: detail.policeReferrals[0]?.policeReferralId ?? "",
    categories: [],
    description: "",
  };
}

/** The server's bounds (server/http/validation/safety.ts). */
export const REASON_MIN = 3;
export const REASON_MAX = 500;
export const TOLD_MIN = 10;
export const TOLD_MAX = 2000;
export const EVIDENCE_MAX = 4000;
export const SHORT_MIN = 2;
export const SHORT_MAX = 200;
export const SHARE_DESCRIPTION_MIN = 5;

const length = (text: string) => text.trim().length;
const between = (text: string, min: number, max: number) => length(text) >= min && length(text) <= max;
const optionalShort = (text: string) => length(text) === 0 || between(text, SHORT_MIN, SHORT_MAX);

function isUrl(text: string): boolean {
  try {
    const url = new URL(text.trim());
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

/** A `datetime-local` value as an ISO instant, or null when it isn't a date. */
export function localToIso(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** The first problem with what was typed, or null when it can be sent. */
export function actionProblem(kind: ReportActionKind, fields: ReportActionFields, now: Date): string | null {
  const reasonOptional = kind === "evidence";
  if (!(reasonOptional && length(fields.reason) === 0) && !between(fields.reason, REASON_MIN, REASON_MAX)) {
    return t("reports.problem.reason", { min: REASON_MIN, max: REASON_MAX });
  }
  const told = () =>
    between(fields.toldReason, TOLD_MIN, TOLD_MAX) ? null : t("reports.problem.told", { min: TOLD_MIN });
  switch (kind) {
    case "evidence":
      if (!between(fields.body, 1, EVIDENCE_MAX)) return t("reports.problem.evidence");
      if (length(fields.evidenceUrl) > 0 && !isUrl(fields.evidenceUrl)) return t("reports.problem.url");
      return null;
    case "verdict": {
      const problem = told();
      if (problem || fields.verdict !== "temporary_suspension") return problem;
      const until = localToIso(fields.suspendedUntil);
      return until && Date.parse(until) > now.getTime() ? null : t("reports.problem.until");
    }
    case "ban":
    case "appeal":
      return told();
    case "police":
      if (!between(fields.station, SHORT_MIN, SHORT_MAX) || !between(fields.officerName, SHORT_MIN, SHORT_MAX)) {
        return t("reports.problem.police");
      }
      return optionalShort(fields.officerContact) && optionalShort(fields.policeReference)
        ? null
        : t("reports.problem.police");
    case "share":
      if (!fields.policeReferralId) return t("reports.problem.referral");
      if (fields.categories.length === 0) return t("reports.problem.categories");
      return length(fields.description) >= SHARE_DESCRIPTION_MIN ? null : t("reports.problem.shareDescription");
    default:
      return null;
  }
}

const optional = (text: string) => (length(text) > 0 ? text.trim() : null);

/** Sends one action on the report as shown, with its intent's key. */
export function sendReportAction(
  client: ConsoleClient,
  detail: SafetyReportDetail,
  kind: ReportActionKind,
  fields: ReportActionFields,
  idempotencyKey: string,
) {
  const id = detail.safetyReportId;
  const version = detail.version;
  const reason = fields.reason.trim();
  const options = { idempotencyKey };
  switch (kind) {
    case "evidence":
      return addReportEvidence(
        client,
        id,
        {
          body: fields.body.trim(),
          evidenceUrl: optional(fields.evidenceUrl),
          version,
          ...(reason ? { reason } : {}),
        },
        options,
      );
    case "severity":
      return setReportSeverity(client, id, { severity: fields.severity, version, reason }, options);
    case "suspend":
      return suspendPendingInvestigation(client, id, { version, reason }, options);
    case "lift":
      return liftSuspension(client, id, { version, reason }, options);
    case "verdict":
      return issueVerdict(
        client,
        id,
        {
          verdict: fields.verdict,
          verdictReason: fields.toldReason.trim(),
          suspendedUntil: fields.verdict === "temporary_suspension" ? localToIso(fields.suspendedUntil) : null,
          version,
          reason,
        },
        options,
      );
    case "ban":
      return banPermanently(client, id, { verdictReason: fields.toldReason.trim(), version, reason }, options);
    case "appeal":
      return decideAppeal(
        client,
        id,
        { decision: fields.decision, decisionReason: fields.toldReason.trim(), version, reason },
        options,
      );
    case "police":
      return referToPolice(
        client,
        id,
        {
          station: fields.station.trim(),
          officerName: fields.officerName.trim(),
          officerContact: optional(fields.officerContact),
          policeReference: optional(fields.policeReference),
          version,
          reason,
        },
        options,
      );
    case "share":
      return shareDataWithPolice(
        client,
        id,
        fields.policeReferralId,
        {
          // In the contract's order, whatever order they were ticked in.
          categories: POLICE_DATA_CATEGORIES.filter((category) => fields.categories.includes(category)),
          description: fields.description.trim(),
          version,
          reason,
        },
        options,
      );
    case "hold":
      return placeLegalHold(client, id, { version, reason }, options);
    case "release":
      return releaseLegalHold(client, id, { version, reason }, options);
  }
}
